/**
 * Marketing Hub Orchestrator
 *
 * Thin composition layer that wires together domain-specific hooks:
 * - useVisitors — visitor state, fetching, selection, import
 * - useLeads — lead state, enrichment, sync, contacts
 * - useLists — list CRUD, add/remove leads, enrich/sync lists
 * - useAssets — asset CRUD, website import
 * - useCampaigns — campaign management, sync
 *
 * Also manages shared state: deals, contacts, mondayUsers, stage transitions,
 * lazy-loading by active tab, and cross-domain orchestration (refreshData).
 */

import { useState, useCallback, useEffect, useRef } from 'react';
// Mock data — only used when useMockData is true (dev/demo mode)
// Lazy-loaded to avoid bundling in production
let apolloVisitorData: import('@/types').Visitor[] = [];
let mondayLeadsData: import('@/types').Lead[] = [];
let mondayAssetsData: import('@/types').MondayAsset[] = [];
let mockCampaigns: import('@/types').Campaign[] = [];
let mockDataLoaded = false;

async function loadMockData() {
  if (mockDataLoaded) return;
  const data = await import('@/data');
  apolloVisitorData = data.apolloVisitorData;
  mondayLeadsData = data.mondayLeadsData;
  mondayAssetsData = data.mondayAssetsData;
  mockCampaigns = data.mockCampaigns;
  mockDataLoaded = true;
}

import { apolloService, mondayService } from '@/services/api';
import { logger } from '@/utils';
import type { TabType, Deal, AsyncState } from '@/types';
import type { StageTransition, MondayContact, MondayUser } from '@/services/api/monday';

import { useVisitors } from './useVisitors';
import { useLeads } from './useLeads';
import { useLists } from './useLists';
import { useAssets } from './useAssets';
import { useCampaigns } from './useCampaigns';

// ============================================================================
// Configuration
// ============================================================================

interface HubConfig {
  useMockData?: boolean;
  activeTab?: TabType; // Passed from router — drives lazy data fetching
  mondayBoardIds?: {
    leads: string;
    accounts?: string;
    deals?: string;
    assets?: string;
    contacts?: string;
  };
}

const DEFAULT_CONFIG: HubConfig = {
  useMockData: false,
  mondayBoardIds: {
    leads: import.meta.env.VITE_MONDAY_LEADS_BOARD_ID || '',
    accounts: import.meta.env.VITE_MONDAY_ACCOUNTS_BOARD_ID || '',
    deals: import.meta.env.VITE_MONDAY_DEALS_BOARD_ID || '',
    assets: import.meta.env.VITE_MONDAY_ASSETS_BOARD_ID || '',
    contacts: import.meta.env.VITE_MONDAY_CONTACTS_BOARD_ID || '',
  },
};

// ============================================================================
// Orchestrator Hook
// ============================================================================

export function useMarketingHub(config: HubConfig = DEFAULT_CONFIG) {
  const { useMockData = false, activeTab: activeTabProp, mondayBoardIds = DEFAULT_CONFIG.mondayBoardIds } = config;
  const initialized = useRef(false);

  // ========================================================================
  // Domain Hooks
  // ========================================================================

  const visitorsHook = useVisitors({
    useMockData,
    initialData: useMockData ? apolloVisitorData : [],
  });

  const leadsHook = useLeads({
    useMockData,
    initialData: useMockData ? mondayLeadsData : undefined,
  });

  const listsHook = useLists({
    leads: leadsHook.leads,
    setLeads: leadsHook.setLeads,
    enrichLeadsParallel: leadsHook.enrichLeadsParallel,
    useMockData,
  });

  const assetsHook = useAssets({
    initialData: useMockData ? mondayAssetsData : [],
  });

  const campaignsHook = useCampaigns({
    leads: leadsHook.leads,
    setLeads: leadsHook.setLeads,
    leadLists: listsHook.leadLists,
    useMockData,
    initialData: useMockData ? mockCampaigns : undefined,
  });

  // ========================================================================
  // Shared State (cross-domain: deals, contacts, users, stage transitions)
  // ========================================================================

  const [deals, setDeals] = useState<AsyncState<Deal[]>>({
    data: [],
    loading: false,
    error: null,
  });

  const [stageTransitions, setStageTransitions] = useState<AsyncState<StageTransition[]>>({
    data: [],
    loading: false,
    error: null,
  });

  const [contacts, setContacts] = useState<AsyncState<MondayContact[]>>({
    data: [],
    loading: false,
    error: null,
  });

  const [mondayUsers, setMondayUsers] = useState<AsyncState<MondayUser[]>>({
    data: [],
    loading: false,
    error: null,
  });

  // ========================================================================
  // UI State
  // ========================================================================

  const [activeTabInternal, setActiveTabInternal] = useState<TabType>('dashboard');
  const activeTab = activeTabProp ?? activeTabInternal;
  const setActiveTab = (tab: TabType) => {
    setActiveTabInternal(tab);
  };

  // ========================================================================
  // Lazy-loading refs & cache
  // ========================================================================

  const dealsFetched = useRef(false);
  const transitionsFetched = useRef(false);
  const contactsFetched = useRef(false);
  const usersFetched = useRef(false);
  const lastFetchedAt = useRef(
    parseInt(sessionStorage.getItem('lastFetchedAt') || '0', 10)
  );
  const CACHE_TTL = 15 * 60 * 1000;

  // ========================================================================
  // Shared Data Fetching
  // ========================================================================

  const fetchDeals = useCallback(async () => {
    setDeals((prev) => ({ ...prev, loading: prev.data.length === 0, error: null }));

    try {
      const data = await mondayService.getDeals();
      setDeals({ data, loading: false, error: null });
    } catch (error) {
      setDeals((prev) => ({
        ...prev,
        loading: false,
        error: (error as Error).message,
      }));
    }
  }, []);

  const fetchStageTransitions = useCallback(async () => {
    setStageTransitions((prev) => ({ ...prev, loading: prev.data.length === 0, error: null }));

    try {
      logger.log('[useMarketingHub] Fetching stage transitions...');
      const data = await mondayService.getActivityLogs({ pages: 20 });
      logger.log(`[useMarketingHub] Stage transitions loaded: ${data.length} transitions`);
      if (data.length > 0) {
        logger.log('[useMarketingHub] Sample transition:', data[0]);
      } else {
        logger.log('[useMarketingHub] No stage transitions found - historical funnel will show current data only');
      }
      setStageTransitions({ data, loading: false, error: null });
    } catch (error) {
      logger.simpleError('[useMarketingHub] Error fetching stage transitions:', error);
      setStageTransitions((prev) => ({
        ...prev,
        loading: false,
        error: (error as Error).message,
      }));
    }
  }, []);

  const fetchContacts = useCallback(async () => {
    setContacts((prev) => ({ ...prev, loading: prev.data.length === 0, error: null }));

    try {
      logger.log('[useMarketingHub] Fetching contacts from Monday.com...');
      const data = await mondayService.getContacts();
      logger.log(`[useMarketingHub] Loaded ${data.length} contacts from Monday.com`);
      setContacts({ data, loading: false, error: null });
    } catch (error) {
      logger.simpleError('[useMarketingHub] Error fetching contacts:', error);
      setContacts((prev) => ({
        ...prev,
        loading: false,
        error: (error as Error).message,
      }));
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setMondayUsers((prev) => ({ ...prev, loading: prev.data.length === 0, error: null }));

    try {
      logger.log('[useMarketingHub] Fetching Monday.com users...');
      const data = await mondayService.getUsers();
      logger.log(`[useMarketingHub] Loaded ${data.length} users from Monday.com`);
      setMondayUsers({ data, loading: false, error: null });
    } catch (error) {
      logger.simpleError('[useMarketingHub] Error fetching users:', error);
      setMondayUsers((prev) => ({
        ...prev,
        loading: false,
        error: (error as Error).message,
      }));
    }
  }, []);

  // ========================================================================
  // Monday Configuration & Initialization
  // ========================================================================

  if (mondayBoardIds && !useMockData) {
    mondayService.configure({
      leadsBoardId: mondayBoardIds.leads,
      accountsBoardId: mondayBoardIds.accounts || undefined,
      dealsBoardId: mondayBoardIds.deals,
      assetsBoardId: mondayBoardIds.assets,
      contactsBoardId: mondayBoardIds.contacts,
    });
  }

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    if (useMockData) {
      loadMockData().then(() => {
        visitorsHook.setVisitors({ data: apolloVisitorData, loading: false, error: null });
        leadsHook.setLeads(prev => ({ ...prev, data: prev.data.length > 0 ? prev.data : mondayLeadsData }));
        assetsHook.setAssets({ data: mondayAssetsData, loading: false, error: null });
      });
    }
  }, [useMockData, mondayBoardIds]);

  // ========================================================================
  // Lazy Loading by Active Tab
  // ========================================================================

  // Visitors are imported via CSV only — no auto-fetch of demo data

  useEffect(() => {
    if ((activeTab === 'leads' || activeTab === 'dashboard') && !leadsHook.fetched.current && !useMockData) {
      const isFresh = Date.now() - lastFetchedAt.current < CACHE_TTL && leadsHook.leads.length > 0;
      leadsHook.fetched.current = true;
      if (!isFresh) {
        lastFetchedAt.current = Date.now();
        sessionStorage.setItem('lastFetchedAt', String(lastFetchedAt.current));
        leadsHook.fetchLeads();
      }
    }
  }, [activeTab, useMockData, leadsHook.fetchLeads]);

  useEffect(() => {
    if ((activeTab === 'dashboard' || activeTab === 'leads') && !dealsFetched.current && !useMockData) {
      const isFresh = Date.now() - lastFetchedAt.current < CACHE_TTL && deals.data.length > 0;
      dealsFetched.current = true;
      if (!isFresh) fetchDeals();
    }
  }, [activeTab, useMockData, fetchDeals]);

  useEffect(() => {
    if (activeTab === 'dashboard' && !transitionsFetched.current && !useMockData) {
      const isFresh = Date.now() - lastFetchedAt.current < CACHE_TTL && stageTransitions.data.length > 0;
      transitionsFetched.current = true;
      if (!isFresh) fetchStageTransitions();
    }
  }, [activeTab, useMockData, fetchStageTransitions]);

  useEffect(() => {
    if (activeTab === 'content' && !assetsHook.fetched.current && !useMockData) {
      const isFresh = Date.now() - lastFetchedAt.current < CACHE_TTL && assetsHook.assets.length > 0;
      assetsHook.fetched.current = true;
      if (!isFresh) assetsHook.fetchAssets();
    }
  }, [activeTab, useMockData, assetsHook.fetchAssets]);

  useEffect(() => {
    if (activeTab === 'leads' && !contactsFetched.current && !useMockData) {
      const isFresh = Date.now() - lastFetchedAt.current < CACHE_TTL && contacts.data.length > 0;
      contactsFetched.current = true;
      if (!isFresh) fetchContacts();
    }
  }, [activeTab, useMockData, fetchContacts]);

  useEffect(() => {
    if (!usersFetched.current && !useMockData) {
      const isFresh = Date.now() - lastFetchedAt.current < CACHE_TTL && mondayUsers.data.length > 0;
      usersFetched.current = true;
      if (!isFresh) fetchUsers();
    }
  }, [useMockData, fetchUsers]);

  // ========================================================================
  // Cross-Domain Orchestration
  // ========================================================================

  const refreshData = useCallback(async (options?: { force?: boolean }) => {
    const force = options?.force ?? false;

    if (useMockData) {
      visitorsHook.setVisitors({ data: apolloVisitorData, loading: false, error: null });
      leadsHook.setLeads({ data: mondayLeadsData, loading: false, error: null });
      assetsHook.setAssets({ data: mondayAssetsData, loading: false, error: null });
      setDeals({ data: [], loading: false, error: null });
      setStageTransitions({ data: [], loading: false, error: null });
    } else {
      if (!force && Date.now() - lastFetchedAt.current < CACHE_TTL) {
        logger.log('[useMarketingHub] Data is fresh (cached), skipping refresh');
        return;
      }
      visitorsHook.fetched.current = true;
      leadsHook.fetched.current = true;
      dealsFetched.current = true;
      transitionsFetched.current = true;
      assetsHook.fetched.current = true;
      contactsFetched.current = true;
      usersFetched.current = true;
      lastFetchedAt.current = Date.now();
      sessionStorage.setItem('lastFetchedAt', String(lastFetchedAt.current));
      await Promise.all([
        leadsHook.fetchLeads(),
        fetchDeals(),
        fetchStageTransitions(),
        assetsHook.fetchAssets(),
        fetchContacts(),
        fetchUsers(),
      ]);
    }
  }, [useMockData, leadsHook.fetchLeads, fetchDeals, fetchStageTransitions, assetsHook.fetchAssets, fetchContacts, fetchUsers]);

  const clearSelections = useCallback(() => {
    visitorsHook.setSelectedVisitors([]);
    leadsHook.setSelectedLeads([]);
  }, []);

  // Wire cross-domain assignCampaignToList: updates both list and campaign
  const assignCampaignToList = useCallback((listId: string, campaignId: string | null) => {
    listsHook.assignCampaignToList(listId, campaignId);
    campaignsHook.assignCampaignToListCampaignSide(listId, campaignId);
  }, [listsHook.assignCampaignToList, campaignsHook.assignCampaignToListCampaignSide]);

  // Wire cross-domain importLeads: creates leads then optionally adds to list
  const importLeads = useCallback(
    async (
      newLeads: Parameters<typeof leadsHook.importLeads>[0],
      listIdOrOptions?: string | { markAsEnriched?: boolean },
      options?: { markAsEnriched?: boolean }
    ) => {
      // Extract listId if provided as string
      let listId: string | undefined;
      if (typeof listIdOrOptions === 'string') {
        listId = listIdOrOptions;
      }

      const result = await leadsHook.importLeads(newLeads, listIdOrOptions, options);

      // If a list ID was provided, add created leads to that list
      if (listId && result.createdLeads && result.createdLeads.length > 0) {
        logger.log('[importLeads] Adding leads to list:', {
          listId,
          leadIds: result.createdLeads.map((l) => l.id),
        });
        listsHook.addToList(listId, 'leads', result.createdLeads.map((l) => l.id));
      }

      return { created: result.created, skipped: result.skipped };
    },
    [leadsHook.importLeads, listsHook.addToList]
  );

  // ========================================================================
  // Computed Values
  // ========================================================================

  const stats = {
    totalVisitors: visitorsHook.visitors.length,
    highIntentVisitors: visitorsHook.visitors.filter((v) => v.intent === 'High').length,
    totalLeads: leadsHook.leads.length,
    totalPipelineValue: leadsHook.leads.reduce((sum, l) => sum + l.value, 0),
    enrichedLeads: leadsHook.leads.filter((l) => l.apolloEnriched).length,
    unenrichedLeads: leadsHook.leads.filter((l) => !l.apolloEnriched).length,
    totalLists: listsHook.leadLists.length,
    totalAssets: assetsHook.assets.length,
    totalCampaigns: campaignsHook.campaigns.length,
    activeCampaigns: campaignsHook.campaigns.filter((c) => c.status === 'Active').length,
    apolloCreditsAvailable: apolloService.getAvailableCredits(),
    cachedEnrichments: apolloService.getEnrichmentCacheStats().count,
  };

  // ========================================================================
  // Return — same interface as before for backward compatibility
  // ========================================================================

  return {
    // Visitors
    visitors: visitorsHook.visitors,
    visitorsLoading: visitorsHook.visitorsLoading,
    visitorsError: visitorsHook.visitorsError,
    visitorPeriods: visitorsHook.visitorPeriods,
    activeVisitorPeriodId: visitorsHook.activePeriodId,
    setActiveVisitorPeriodId: visitorsHook.setActivePeriodId,
    deleteVisitorPeriod: visitorsHook.deleteVisitorPeriod,
    previousVisitorDomains: visitorsHook.previousDomains,

    // Leads
    leads: leadsHook.leads,
    leadsLoading: leadsHook.leadsLoading,
    leadsError: leadsHook.leadsError,

    // Shared data
    deals: deals.data,
    dealsLoading: deals.loading,
    dealsError: deals.error,

    stageTransitions: stageTransitions.data,
    stageTransitionsLoading: stageTransitions.loading,
    stageTransitionsError: stageTransitions.error,

    // Assets
    assets: assetsHook.assets,
    assetsLoading: assetsHook.assetsLoading,
    assetsError: assetsHook.assetsError,

    // Contacts
    contacts: contacts.data,
    contactsLoading: contacts.loading,
    contactsError: contacts.error,

    // Monday Users
    mondayUsers: mondayUsers.data,
    mondayUsersLoading: mondayUsers.loading,
    mondayUsersError: mondayUsers.error,

    // Lists & Campaigns
    leadLists: listsHook.leadLists,
    campaigns: campaignsHook.campaigns,

    // Stats
    stats,

    // UI State
    activeTab,
    setActiveTab,
    selectedVisitors: visitorsHook.selectedVisitors,
    selectedLeads: leadsHook.selectedLeads,

    // Modal State
    selectedVisitor: visitorsHook.selectedVisitor,
    setSelectedVisitor: visitorsHook.setSelectedVisitor,
    selectedLead: leadsHook.selectedLead,
    setSelectedLead: leadsHook.setSelectedLead,
    selectedAsset: assetsHook.selectedAsset,
    setSelectedAsset: assetsHook.setSelectedAsset,
    selectedList: listsHook.selectedList,
    setSelectedList: listsHook.setSelectedList,

    // Action States
    enrichingLeads: leadsHook.enrichingLeads,
    syncingContacts: leadsHook.syncingContacts,
    enrichingLists: listsHook.enrichingLists,
    syncingLists: listsHook.syncingLists,
    syncingCampaigns: campaignsHook.syncingCampaigns,
    syncingLeadsToMonday: leadsHook.syncingLeadsToMonday,
    importingVisitors: visitorsHook.importingVisitors,

    // Actions
    refreshData,
    lastSyncedAt: lastFetchedAt.current || null,
    toggleVisitorSelection: visitorsHook.toggleVisitorSelection,
    toggleLeadSelection: leadsHook.toggleLeadSelection,
    clearSelections,
    enrichLead: leadsHook.enrichLead,
    bulkEnrichLeads: leadsHook.bulkEnrichLeads,
    syncContactsToLeads: leadsHook.syncContactsToLeads,
    importVisitors: visitorsHook.importVisitors,
    importLeads,
    createList: listsHook.createList,
    deleteList: listsHook.deleteList,
    markListEnriched: listsHook.markListEnriched,
    markLeadEnriched: leadsHook.markLeadEnriched,
    addToList: listsHook.addToList,
    removeFromList: listsHook.removeFromList,
    assignCampaignToList,
    enrichList: listsHook.enrichList,
    syncListToMonday: listsHook.syncListToMonday,
    syncCampaignToMonday: campaignsHook.syncCampaignToMonday,
    syncEnrichedLeadsToMonday: leadsHook.syncEnrichedLeadsToMonday,

    // Helper functions
    getListsForLead: listsHook.getListsForLead,
    getCampaignsForLead: campaignsHook.getCampaignsForLead,

    // Asset Actions
    createAsset: assetsHook.createAsset,
    updateAsset: assetsHook.updateAsset,
    deleteAsset: assetsHook.deleteAsset,
    importCaseStudiesFromWebsite: assetsHook.importCaseStudiesFromWebsite,

    // Campaign Actions
    createCampaign: campaignsHook.createCampaign,
    updateCampaign: campaignsHook.updateCampaign,
    deleteCampaign: campaignsHook.deleteCampaign,

    // Campaign Step Actions
    addCampaignStep: campaignsHook.addCampaignStep,
    updateCampaignStep: campaignsHook.updateCampaignStep,
    deleteCampaignStep: campaignsHook.deleteCampaignStep,
    reorderCampaignSteps: campaignsHook.reorderCampaignSteps,

    // Config
    useMockData,
  };
}

export type UseMarketingHubReturn = ReturnType<typeof useMarketingHub>;
